import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, Bell, LogOut, Shield, Trash2, Loader2, Clock, Ban } from "lucide-react";
import { isNative, authHeaders, clearAuthToken, apiBase } from "@/lib/native";
import {
  getDailyPref,
  checkPermission,
  requestPermission,
  enableDailyReminder,
  disableDailyReminder,
} from "@/lib/reminders";

const API = `${apiBase()}/api`;

const api = async (path, options = {}) => {
  const extra = await authHeaders();
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...extra },
    ...options,
  });
  if (!res.ok) throw new Error(String(res.status));
  return res.json().catch(() => ({}));
};

export default function Settings() {
  const navigate = useNavigate();
  const native = isNative();
  const [user, setUser] = useState(undefined);
  const [pref, setPref] = useState({ enabled: false, hour: 8, minute: 30 });
  const [permNote, setPermNote] = useState("");
  const [version, setVersion] = useState("1.0.0");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [unblocking, setUnblocking] = useState("");

  useEffect(() => {
    (async () => {
      let u = null;
      try { u = await api("/community/auth/me"); } catch (e) { u = null; }
      setUser(u);
      if (u) setBlocked(u.blocked || []);
    })();
    getDailyPref().then(setPref);
    if (native) {
      import("@capacitor/app")
        .then(({ App }) => App.getInfo())
        .then((info) => setVersion(info.version))
        .catch(() => {});
    }
  }, [native]);

  const timeStr = `${String(pref.hour).padStart(2, "0")}:${String(pref.minute).padStart(2, "0")}`;

  const onToggleDaily = async () => {
    if (pref.enabled) {
      await disableDailyReminder();
      setPref((p) => ({ ...p, enabled: false }));
      setPermNote("");
      return;
    }
    let perm = await checkPermission();
    if (perm !== "granted") perm = await requestPermission();
    if (perm !== "granted") {
      setPermNote("Notifications are off in your system settings. Turn them on for Command Center to get reminders.");
      return;
    }
    await enableDailyReminder(pref.hour, pref.minute);
    setPref((p) => ({ ...p, enabled: true }));
    setPermNote("");
  };

  const onTimeChange = async (val) => {
    const [h, m] = val.split(":").map((n) => parseInt(n, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    setPref((p) => ({ ...p, hour: h, minute: m }));
    if (pref.enabled) await enableDailyReminder(h, m);
  };

  const openPrivacy = async () => {
    const url = "https://commandcenteradhd.com/privacy";
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url });
    } catch (e) {
      window.open(url, "_system");
    }
  };

  const logout = async () => {
    try { await api("/community/auth/logout", { method: "POST" }); } catch (e) { /* */ }
    await clearAuthToken();
    navigate("/today");
  };

  const unblock = async (uid) => {
    setUnblocking(uid);
    try {
      await api("/community/unblock", { method: "POST", body: JSON.stringify({ uid }) });
      setBlocked((list) => list.filter((b) => b.uid !== uid));
    } catch (e) { /* no-op */ }
    setUnblocking("");
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api("/community/auth/account", { method: "DELETE" });
      await clearAuthToken();
      navigate("/today");
    } catch (e) {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const rootStyle = { overscrollBehavior: "none" };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F8FA]" style={rootStyle} data-testid="settings-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#15816B]" aria-hidden="true" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#F6F8FA] px-6 text-center font-body text-[#0B132B]" style={rootStyle} data-testid="settings-signed-out">
        <p className="text-base text-[#5A6472]">You are signed out.</p>
        <Link to="/today" className="rounded-xl bg-[#0B132B] px-5 py-3 font-heading text-sm font-bold text-white">Go to Today</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F8FA] font-body text-[#0B132B]" style={rootStyle} data-testid="settings-page">
      <header
        className="sticky top-0 z-20 border-b border-[#12203c] bg-[#0B132B] px-5 py-4 text-white"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 1rem)" }}
      >
        <div className="mx-auto flex max-w-md items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/today")}
            data-testid="settings-back"
            aria-label="Back to Today"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 transition-colors hover:bg-white/20"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <p className="font-heading text-lg font-extrabold leading-none">Settings</p>
        </div>
      </header>

      <main className="mx-auto max-w-md space-y-5 px-5 pb-16 pt-6">
        {/* Reminders */}
        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="settings-reminders">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#15816B]" aria-hidden="true" />
            <h2 className="font-heading text-base font-extrabold">Reminders</h2>
          </div>
          {native ? (
            <>
              <div className="mt-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold">Daily nudge</p>
                  <p className="text-xs text-[#8A94A6]">A gentle reminder to pick today's one thing.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={pref.enabled}
                  onClick={onToggleDaily}
                  data-testid="settings-daily-toggle"
                  className={`relative h-7 w-12 flex-shrink-0 rounded-full transition-colors ${pref.enabled ? "bg-[#15816B]" : "bg-[#CBD3DE]"}`}
                >
                  <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${pref.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {pref.enabled && (
                <div className="mt-4 flex items-center gap-2" data-testid="settings-daily-time">
                  <Clock className="h-4 w-4 text-[#8A94A6]" aria-hidden="true" />
                  <label htmlFor="daily-time" className="text-sm text-[#5A6472]">Remind me at</label>
                  <input
                    id="daily-time"
                    type="time"
                    value={timeStr}
                    onChange={(e) => onTimeChange(e.target.value)}
                    data-testid="settings-daily-time-input"
                    className="rounded-lg border border-[#D9DEE6] bg-[#FAFBFC] px-3 py-1.5 text-sm outline-none focus:border-[#15816B]"
                  />
                </div>
              )}
              {permNote && <p className="mt-3 text-xs text-[#B4443E]" data-testid="settings-perm-note">{permNote}</p>}
              <p className="mt-3 text-xs text-[#8A94A6]">Time blocks can also remind you 5 minutes before, from the Today screen.</p>
            </>
          ) : (
            <p className="mt-3 text-sm text-[#8A94A6]" data-testid="settings-reminders-web-note">
              Reminders are available in the Command Center mobile app.
            </p>
          )}
        </section>

        {/* Blocked members */}
        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="settings-blocked">
          <div className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-[#15816B]" aria-hidden="true" />
            <h2 className="font-heading text-base font-extrabold">Blocked members</h2>
          </div>
          {blocked.length === 0 ? (
            <p className="mt-3 text-sm text-[#8A94A6]" data-testid="settings-blocked-empty">
              You have not blocked anyone. Blocked members cannot message you, and you will not see their posts.
            </p>
          ) : (
            <ul className="mt-4 space-y-3" data-testid="settings-blocked-list">
              {blocked.map((b) => (
                <li key={b.uid} className="flex items-center justify-between gap-3" data-testid={`settings-blocked-${b.uid}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    {b.avatar_url ? (
                      <img src={b.avatar_url} alt="" width="36" height="36" className="h-9 w-9 flex-shrink-0 rounded-full object-cover" />
                    ) : (
                      <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#E6E9EE] text-sm font-bold text-[#5A6472]">
                        {(b.display_name || "?").slice(0, 1).toUpperCase()}
                      </span>
                    )}
                    <span className="truncate text-sm font-semibold">{b.display_name || "Member"}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => unblock(b.uid)}
                    disabled={unblocking === b.uid}
                    data-testid={`settings-unblock-${b.uid}`}
                    className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-[#D9DEE6] px-3 py-1.5 text-xs font-bold text-[#0B132B] transition-colors hover:bg-[#F0F3F7]"
                  >
                    {unblocking === b.uid ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unblock"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Account */}
        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="settings-account">
          <h2 className="font-heading text-base font-extrabold">Account</h2>
          <p className="mt-1 text-sm text-[#5A6472]" data-testid="settings-email">{user.email}</p>
          <button
            type="button"
            onClick={logout}
            data-testid="settings-signout"
            className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#D9DEE6] bg-white px-4 py-2.5 font-heading text-sm font-bold text-[#0B132B] transition-colors hover:bg-[#F1F4F8]"
          >
            <LogOut className="h-4 w-4 text-[#5A6472]" aria-hidden="true" /> Sign out
          </button>
        </section>

        {/* About / links */}
        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="settings-about">
          {native ? (
            <button
              type="button"
              onClick={openPrivacy}
              data-testid="settings-privacy-link"
              className="flex items-center gap-2 text-sm font-semibold text-[#15816B]"
            >
              <Shield className="h-4 w-4" aria-hidden="true" /> Privacy policy
            </button>
          ) : (
            <a
              href="/privacy"
              data-testid="settings-privacy-link"
              className="flex items-center gap-2 text-sm font-semibold text-[#15816B]"
            >
              <Shield className="h-4 w-4" aria-hidden="true" /> Privacy policy
            </a>
          )}
          <p className="mt-4 text-xs text-[#8A94A6]" data-testid="settings-version">Command Center v{version}</p>
        </section>

        {/* Delete account */}
        <section className="rounded-2xl border border-[#F3D3D1] bg-white p-5" data-testid="settings-danger">
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-[#B4443E]" aria-hidden="true" />
            <h2 className="font-heading text-base font-extrabold text-[#B4443E]">Delete account</h2>
          </div>
          <p className="mt-1 text-xs text-[#8A94A6]">
            Permanently removes your account, your daily entries, and signs you out. This cannot be undone.
          </p>
          {!confirmDelete ? (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              data-testid="settings-delete-open"
              className="mt-4 inline-flex items-center gap-2 rounded-xl border border-[#E2A9A5] bg-white px-4 py-2.5 font-heading text-sm font-bold text-[#B4443E] transition-colors hover:bg-[#FDF3F2]"
            >
              Delete my account
            </button>
          ) : (
            <div className="mt-4 rounded-xl bg-[#FDF3F2] p-4" data-testid="settings-delete-confirm">
              <p className="text-sm font-semibold text-[#7a2622]">Are you sure? This is permanent.</p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={deleteAccount}
                  disabled={deleting}
                  data-testid="settings-delete-confirm-yes"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#B4443E] px-3.5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#9c3a35] disabled:opacity-60"
                >
                  {deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  Yes, delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  data-testid="settings-delete-cancel"
                  className="rounded-lg border border-[#D9DEE6] bg-white px-3.5 py-2 text-sm font-semibold text-[#5A6472] transition-colors hover:bg-[#F1F4F8]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
